<script lang="ts">
export default {
	inheritAttrs: false,
};
</script>

<script setup lang="ts">
import { propsFactory, useResponsiveContainer } from "@soft-db/soft-utils";
import { useWindowSize } from "@vueuse/core";
import { v4 as uuidv4 } from "uuid";
import { computed, ref, watch, type PropType } from "vue";
import { useLocale } from "../";
import type {
	ContainerMargins,
	SVGContainerOptions,
	SVGPosition,
} from "../interfaces/svg-container";
import { useMousePosition } from "../use/mouse-position";
import { SVGEventEmitter, provideSVGContainer } from "../use/svg-container";
import { useZoomBehavior } from "../use/zoom-behavior";

const props = defineProps(
	propsFactory<SVGContainerOptions>()({
		margins: {
			type: Object as PropType<ContainerMargins>,
			default: () => ({ top: 0, bottom: 0, left: 0, right: 0 }),
		},
		tooltip: Boolean,
		disableGesture: Boolean,
	})
);

const emit = defineEmits<{
	(e: "click", payload: SVGPosition): void;
	(e: "dblclick", payload: SVGPosition): void;
	(e: "click:outside"): void;
}>();

const div = ref<HTMLDivElement>();

const { dimension } = useResponsiveContainer(div);
const contentDimension = computed(() => {
	const { width, height } = dimension.value;
	const { top, bottom, left, right } = props.margins;

	return {
		width: Math.max(width - left - right, 0),
		height: Math.max(height - top - bottom, 0),
	};
});

const mouseTracker = ref<SVGGElement>();

const disableGesture = computed(() => props.disableGesture);
const _zoomTracker = computed(() => {
	if (disableGesture.value) return undefined;

	return mouseTracker.value;
});

const { transform, setTransform, zoomPrevented } = useZoomBehavior({
	dimension: contentDimension,
	trackerRef: _zoomTracker,
});

const showZoomPreventedHint = ref(false);
watch(zoomPrevented, (_, __, cleanup) => {
	showZoomPreventedHint.value = true;

	const timeout = setTimeout(() => {
		showZoomPreventedHint.value = false;
	}, 750);

	cleanup(() => {
		clearTimeout(timeout);
	});
});

// Zoom changed, hide ctrl zoom overlay
watch(transform, () => {
	showZoomPreventedHint.value = false;
});

const mouse = useMousePosition(mouseTracker);
const clickOutsideEvent = SVGEventEmitter();

const api = {
	dimension,
	contentDimension,
	mouse,
	transform,
	setTransform,
	clickOutsideEvent,
};
defineExpose(api);
provideSVGContainer(api);

const contentAttrs = computed(() => {
	const { left, top } = props.margins;

	return {
		transform: `translate(${left}, ${top})`,
	};
});
const clipId = `clip-${uuidv4()}`;

const tooltipStyle = computed(() => ({
	left: `${mouse.position.value.x + 60}px`,
	top: `${mouse.position.value.y - 20}px`,
}));

const mousePosition = computed(() => mouse.position.value);
const mouseIn = computed(() => mouse.inBounds.value);

const clickOutside = () => {
	clickOutsideEvent.emit();
	emit("click:outside");

	showZoomPreventedHint.value = false;
};

const isMac = navigator.userAgent.indexOf("Intel Mac") !== -1;
const { width } = useWindowSize();
const isMobile = computed(() => width.value < 640);

const { t } = useLocale();
</script>

<template>
	<div ref="div" class="absolute inset-0">
		<Transition name="fade">
			<div
				v-if="showZoomPreventedHint"
				:class="{
					'pointer-events-none absolute inset-0 flex items-center justify-center': true,
					'text-md bg-black/[0.45] text-center font-bold text-white md:text-2xl': true,
				}"
			>
				{{
					isMobile
						? t("zoom-touch-needed")
						: isMac
							? t("zoom-ctrl-needed.mac")
							: t("zoom-ctrl-needed.windows")
				}}
			</div>
		</Transition>
		<svg class="pointer-events-none" v-bind="{ ...dimension }">
			<g ref="mouseTracker" v-bind="contentAttrs">
				<rect
					class="pointer-events-auto fill-transparent"
					v-bind="contentDimension"
					@click="clickOutside"
				/>
				<defs>
					<clipPath :id="clipId">
						<rect v-bind="contentDimension" />
					</clipPath>
					<filter id="drop-shadow" height="100%">
						<!-- http://bl.ocks.org/cpbotha/5200394 -->
						<feDropShadow stdDeviation="5" floodColor="#000" floodOpacity="1" />
					</filter>
				</defs>
				<g class="pointer-events-none">
					<g>
						<slot />
					</g>
					<g :clip-path="`url(#${clipId})`">
						<slot name="clip" />
					</g>
				</g>
			</g>
		</svg>
		<div
			v-if="tooltip && mouseIn"
			class="pointer-events-none absolute z-10 p-2"
			:style="tooltipStyle"
		>
			<slot name="tooltip" :x="mousePosition.x" :y="mousePosition.y" />
		</div>
	</div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.15s ease-in;
}

.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}
</style>
